import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../logic/store/Store';
import { Api } from '../../../logic/Api/Api';
import { useTheme } from 'styled-components';
import { ScaleLoader } from 'react-spinners';
import { IStarterPakProgress, IGrowerStatus, CurrentStatuses, GrowerStatusDetails } from './UserManagementTool';

// Constants for configuration 
const POLLING_INTERVAL_MS = 3000;
const MODAL_WIDTH = 720;
const MAX_HEIGHT = 480;

/**
 * Sorts growers by status priority and name for optimal display order.
 * Priority: Running > Failed > Completed > NotStarted
 * Within each status group, growers are sorted alphabetically by name.
 */
const sortGrowersByStatus = (growers: IGrowerStatus[]): IGrowerStatus[] => 
{
	const statusOrder: CurrentStatuses[] = [
		CurrentStatuses.Running,
		CurrentStatuses.Failed,
		CurrentStatuses.Completed,
		CurrentStatuses.NotStarted,
	];

	return growers.slice().sort((a, b) => 
	{
		if (a.CurrentWorkflowStatus === b.CurrentWorkflowStatus) 
		{
			return a.Name.localeCompare(b.Name);
		}
		return statusOrder.indexOf(a.CurrentWorkflowStatus) - statusOrder.indexOf(b.CurrentWorkflowStatus);
	});
};

/**
 * Modal component for displaying real-time StarterPAK process progress.
 * 
 * Manages a long-running process that provisions StarterPAK data for growers,
 * displaying progress through polling and real-time updates. The modal shows
 * grower-level status, error messages, and completion statistics.
 * 
 * Key behaviors:
 * - Automatically starts the PAK process when a user ID is provided
 * - Polls progress every 3 seconds until completion or failure
 * - Displays growers sorted by status priority (Running > Failed > Completed > NotStarted)
 */
export function StarterPakModal(props: IStarterPakModalProps) 
{
	const { scanUserId, onClose } = props;
	const userAuthToken = useSelector((state: RootState) => state.auth.userAuthToken);
	const theme = useTheme();

	// Ref for cleanup management to prevent memory leaks
	const isUnmountedRef = useRef(false);
	const timeoutRef = useRef<NodeJS.Timeout>();

	const api6 = useMemo(() => new Api('/api/6', userAuthToken, undefined), [userAuthToken]);
	const [showStarterPakModal, setShowStarterPakModal] = useState(false);
	const [starterPakError, setStarterPakError] = useState<string>();
	const [starterPakProgress, setStarterPakProgress] = useState<IStarterPakProgress>();
	const [growerProgress, setGrowerProgress] = useState<IGrowerStatus[]>([]);

	// Cleanup effect to prevent memory leaks
	useEffect(() => 
	{
		return () => 
		{
			isUnmountedRef.current = true;
			if (timeoutRef.current) 
			{
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const summary = useMemo(() => 
	{
		const totalGrowerCount = growerProgress.length;
		const runningGrowerCount = growerProgress.filter(g => g.CurrentWorkflowStatus === 'Running').length;
		const failedGrowerCount = growerProgress.filter(g => g.CurrentWorkflowStatus === 'Failed').length;
		const completedGrowerCount = growerProgress.filter(g => g.CurrentWorkflowStatus === 'Completed').length;

		return {
			total: totalGrowerCount,
			running: runningGrowerCount,
			failed: failedGrowerCount,
			completed: completedGrowerCount
		};
	}, [growerProgress]);


	/**
	 * Manages the StarterPAK process lifecycle including initiation and progress polling.
	 * Implements polling with cleanup to prevent memory leaks and handles all error scenarios.
	 */
	const starterPAK = useCallback((userId: string) => 
	{
		async function exec() 
		{
			// Reset state for new process
			setGrowerProgress([]);
			setStarterPakError(undefined);
			setShowStarterPakModal(true);

			try 
			{
				const response = await api6.postAsync(`users/${userId}/starterpaks`, {});
				if (!response.Success) 
				{
					setStarterPakError(response.ErrorMessage || 'Failed to start PAK process');
					return;
				}
			}
			catch 
			{
				setStarterPakError('An error occurred while starting the PAK process');
				return;
			}

			// Polling loop with proper cleanup handling
			let done = false;
			do 
			{
				// Check if component was unmounted during polling
				if (isUnmountedRef.current) 
				{
					break;
				}

				try 
				{
					const pollResponse = await api6.getAsync<IStarterPakProgress>(`users/${userId}/starterpaks/progress`);
					
					// Check again after async operation
					if (isUnmountedRef.current) 
					{
						break;
					}

					if (!pollResponse.Success) 
					{
						setStarterPakError(pollResponse.ErrorMessage || 'An unknown error occurred');
						done = true;
						break;
					}

					setStarterPakProgress(pollResponse.Data);
					const sortedGrowers = sortGrowersByStatus(Object.values(pollResponse.Data.GrowerStatuses));
					setGrowerProgress(sortedGrowers);

					// Check if process completed
					const isComplete = pollResponse.Data.CurrentStatus === CurrentStatuses.Completed || 
										pollResponse.Data.CurrentStatus === CurrentStatuses.Failed;
					if (isComplete) 
					{
						done = true;
						break;
					}
				}
				catch (error)
				{
					if (isUnmountedRef.current) 
					{
						break;
					}
					setStarterPakError('An error occurred while polling the PAK process');
					done = true;
					break;
				}

				// Use timeout ref for proper cleanup
				if (!done && !isUnmountedRef.current) 
				{
					await new Promise(resolve => 
					{
						timeoutRef.current = setTimeout(resolve, POLLING_INTERVAL_MS);
					});
				}
			} while (!done && !isUnmountedRef.current);
		}

		exec();
	}, [api6]);

	useEffect(() => 
	{
		if (scanUserId) 
		{
			starterPAK(scanUserId ?? '');
		}
	},
	[scanUserId, starterPAK]);

	const close = useCallback(() => 
	{
		setShowStarterPakModal(false);
		onClose();
	}, [onClose]);

	return <Modal
		open={showStarterPakModal}
		closable={true}
		onOk={close}
		onCancel={close}
		cancelButtonProps={{ style: { display: 'none' } }}
		width={MODAL_WIDTH}
	>
		<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
			<h2>StarterPAK Progress ({summary?.completed} success, {summary?.failed} failed, {summary?.total} total)</h2>
			<ScaleLoader color={theme.colors.primary} loading={starterPakProgress?.CurrentStatus !== 'Completed' && starterPakProgress?.CurrentStatus !== 'Failed'} />
		</div>
		{starterPakError && <div style={{ color: 'red' }}>{starterPakError}</div>}
		{starterPakProgress && <h3>{starterPakProgress.CurrentStatus}</h3>}
		<div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr max-content', maxHeight: MAX_HEIGHT, overflow: 'auto' }}>
			{growerProgress && Object.values(growerProgress).map((status, index) => (
				<React.Fragment key={`grower-status-${index}`}>
					<div style={{ backgroundColor: theme.colors.lightGrey, marginTop: 4, gridColumnStart: 1, gridColumnEnd: 3, color: status.CurrentWorkflowStatus === 'Failed' ? 'darkred' : 'black' }}>
						<b>{status.Name} ({status.FoundationId})</b>
					</div>
					<div style={{ backgroundColor: theme.colors.lightGrey, marginTop: 4, gridColumn: 3, color: status.CurrentWorkflowStatus === 'Failed' ? 'darkred' : 'black' }}>
						<b>{status.CurrentWorkflowStatus}</b>
					</div>
					<div style={{ gridColumn: 1 }}><b>Start:</b> {GrowerStatusDetails(status.InitialGrowerStatus)}</div>
					<div style={{ gridColumn: 3 }}><b>End:</b> {GrowerStatusDetails(status.FinalGrowerStatus)}</div>
					<div style={{ gridColumnStart: 1, gridColumnEnd: 4, marginBottom: 8 }}>{status.Message}</div>

				</React.Fragment>
			))}
		</div>
	</Modal>;
}


interface IStarterPakModalProps {
	/**
	 * The ID of the user to scan for StarterPAK provisioning. 
 	 * When provided (not undefined), triggers the PAK process automatically.
	 */
	scanUserId: string | undefined;

	/**
	 * Callback function invoked when the modal should be closed.
	 */
	onClose: () => void;
}
